// StartAssessmentDialogV2.jsx - Enhanced with Assessment Type & Inline Model Registration

import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Modal, Stack, Group, Text, Button, Card, Badge,
  Select, Divider, Alert, Title, Grid, Switch, TextInput,
  ThemeIcon
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  IconRobot, IconSettings, IconRocket, IconInfoCircle, IconPlus,
  IconTool, IconCompass, IconGlobe, IconFileDescription, IconBolt
} from '@tabler/icons-react';
import { fetchGlobalConfig } from './redux/actions/globalConfigActions';
import { isRiskManager } from './utils/roleHelpers';
import { notifications } from '@mantine/notifications';
import {
  fetchAllAssessments, createAssessment,
  selectCompletedBuilderAssessments, selectCompletedUseCaseAssessments
} from './redux/slices/assessmentsSlice';
import {
  fetchAllModels, createModel,
  selectModelsWithoutBuilderAssessment, selectModelsWithBuilderStatus
} from './redux/slices/modelsSlice';

const StartAssessmentDialogV2 = ({ open, onClose, preselectedModel = null }) => {
  const modelsWithoutBuilder = useSelector(selectModelsWithoutBuilderAssessment);
  const modelsWithBuilderStatus = useSelector(selectModelsWithBuilderStatus);
  const allTemplates = useSelector((state) => state.templates.allTemplates);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useAuth0();

  // Check if user is Risk Manager
  const userIsRiskManager = useMemo(() => {
    const userRoles = user?.[`${window.location.origin}/roles`] || user?.['https://agenthuginn.ai/roles'] || user?.['https://riskgpt.app/roles'] || [];
    return isRiskManager(userRoles);
  }, [user]);

  // Assessment type: 'builder' | 'use_case' | 'business_case'
  const [assessmentType, setAssessmentType] = useState('builder');

  // Model source: 'existing' or 'new'
  const [modelSource, setModelSource] = useState('existing');

  // New model registration data
  const [newModelData, setNewModelData] = useState({
    modelName: '',
    useCaseShortDescription: '',
    modelType: '',
    modelStage: ''
  });

  const [selectedMode, setSelectedMode] = useState('autopilot');
  const [isAutonomous, setIsAutonomous] = useState(false);
  const [assessmentMode, setAssessmentMode] = useState('prompt');
  const [isStarting, setIsStarting] = useState(false);
  const [userEditedName, setUserEditedName] = useState(false);

  // Parent assessment UUID: Builder UUID for Navigator, Navigator UUID for Explorer
  const [parentAssessmentUuid, setParentAssessmentUuid] = useState('');

  const form = useForm({
    initialValues: {
      selectedModel: preselectedModel?.modelId || '',
      assessmentName: '',
      selectedTemplate: '',
    },
    validate: {
      selectedModel: (value) => {
        if (modelSource === 'existing' && !value) return 'Please select a model';
        return null;
      },
      assessmentName: (value) => (!value || !value.trim() ? 'Assessment name is required' : null),
      selectedTemplate: (value) => (!value ? 'Please select a template' : null),
    },
  });

  // Ref to store form setFieldValue for stable useEffect callbacks
  const formRef = useRef(form);
  formRef.current = form;

  // Stable callbacks for form operations
  const setSelectedModel = useCallback((value) => {
    formRef.current.setFieldValue('selectedModel', value);
  }, []);

  const setAssessmentName = useCallback((value) => {
    formRef.current.setFieldValue('assessmentName', value);
  }, []);

  // Fetch model form config when dialog opens
  React.useEffect(() => {
    if (open) {
      dispatch(fetchGlobalConfig('modelFormConfig'));
    }
  }, [dispatch, open]);

  // Get available models based on assessment type
  const availableModels = useMemo(() => {
    if (assessmentType === 'builder') {
      // Builder: Only show models WITHOUT Builder assessments
      return modelsWithoutBuilder.map(model => ({
        ...model,
        modelId: model.model_uuid,
        modelName: model.model_name || 'Unnamed Model',
        modelType: model.model_type || 'Unknown Type',
        modelStage: model.model_stage || 'Unknown Stage',
        useCaseShortDescription: model.use_case_short_description || '',
      }));
    } else {
      // Navigator/Explorer: Show ALL models (parent is selected separately)
      return modelsWithBuilderStatus.map(model => ({
        ...model,
        modelId: model.model_uuid,
        modelName: model.model_name || 'Unnamed Model',
        modelType: model.model_type || 'Unknown Type',
        modelStage: model.model_stage || 'Unknown Stage',
        useCaseShortDescription: model.use_case_short_description || '',
        builderAssessment: model.builderAssessment || null,
      }));
    }
  }, [assessmentType, modelsWithoutBuilder, modelsWithBuilderStatus]);

  // Get completed Builder assessments for Navigator parent selection
  const completedBuilders = useSelector(selectCompletedBuilderAssessments);
  const completedBuilderOptions = useMemo(() => {
    if (assessmentType !== 'use_case') return [];
    return completedBuilders
      .map(a => ({
        value: a.info?.assessment_uuid,
        label: `${a.model_name || 'Unknown'} - ${a.info?.assessment_name || 'Builder Assessment'}`,
        modelUuid: a.model_uuid,
        modelName: a.model_name,
        assessmentUuid: a.info?.assessment_uuid,
        assessmentName: a.info?.assessment_name,
      }))
      .filter(a => a.value);
  }, [assessmentType, completedBuilders]);

  // Get completed Navigator assessments for Explorer parent selection
  const completedNavigators = useSelector(selectCompletedUseCaseAssessments);
  const completedNavigatorOptions = useMemo(() => {
    if (assessmentType !== 'business_case') return [];
    return completedNavigators
      .map(a => ({
        value: a.info?.assessment_uuid,
        label: `${a.model_name || 'Unknown'} - ${a.info?.assessment_name || 'Navigator Assessment'}`,
        modelUuid: a.model_uuid,
        modelName: a.model_name,
        assessmentUuid: a.info?.assessment_uuid,
        assessmentName: a.info?.assessment_name,
      }))
      .filter(a => a.value);
  }, [assessmentType, completedNavigators]);

  // Update form when preselectedModel changes
  useEffect(() => {
    if (preselectedModel) {
      setSelectedModel(preselectedModel.modelId);
      setAssessmentName(`${preselectedModel.modelName} Assessment`);
      setUserEditedName(false);
    }
  }, [preselectedModel, setSelectedModel, setAssessmentName]);

  // Auto-suggest assessment name when model or new model name changes
  useEffect(() => {
    if (userEditedName) return;

    if (modelSource === 'existing') {
      const modelId = form.values.selectedModel;
      if (modelId) {
        const model = availableModels.find(m => m.modelId === modelId);
        if (model) {
          setAssessmentName(`${model.modelName} Assessment`);
        }
      }
    } else if (newModelData.modelName) {
      setAssessmentName(`${newModelData.modelName} Assessment`);
    }
  }, [form.values.selectedModel, newModelData.modelName, modelSource, availableModels, userEditedName, setAssessmentName]);

  // Reset selections when assessment type changes
  useEffect(() => {
    setSelectedModel('');
    setUserEditedName(false);
    setParentAssessmentUuid('');
    setModelSource('existing');
  }, [assessmentType, setSelectedModel]);

  // Convert templates array to options for Select component, filtered by assessment type
  const templateOptions = useMemo(() => {
    const templatesArray = allTemplates || [];
    const publishedTemplates = templatesArray.filter(t => t.is_published);
    const filteredTemplates = publishedTemplates.filter(t => {
      if (assessmentType === 'use_case') {
        return t.model_type === 'use_case';
      } else if (assessmentType === 'business_case') {
        // Explorer: match both legacy 'explorer' and new 'business_case'
        return t.model_type === 'business_case' || t.model_type === 'explorer';
      } else {
        // Builder: show generative, predictive, custom
        return t.model_type !== 'explorer' && t.model_type !== 'use_case' && t.model_type !== 'business_case';
      }
    });
    return filteredTemplates.map(t => ({
      value: t.id,
      label: `${t.name}${t.model_type ? ` - ${t.model_type}` : ''}`,
      description: t.description
    }));
  }, [allTemplates, assessmentType]);

  const handleStartAssessment = async () => {
    // Validate form
    if (!form.isValid()) {
      form.validate();
      return;
    }

    // Validate new model data if registering new model (Builder only)
    if (assessmentType === 'builder' && modelSource === 'new') {
      if (!newModelData.modelName || !newModelData.modelType || !newModelData.modelStage) {
        notifications.show({
          title: 'Missing Fields',
          message: 'Please fill in all required model fields (Name, Type, Stage)',
          color: 'red'
        });
        return;
      }
    }

    // Navigator requires a completed Builder assessment
    if (assessmentType === 'use_case' && !parentAssessmentUuid) {
      notifications.show({
        title: 'Builder Assessment Required',
        message: 'Please select a completed Builder assessment to link.',
        color: 'yellow'
      });
      return;
    }

    // Explorer requires a completed Navigator assessment
    if (assessmentType === 'business_case' && !parentAssessmentUuid) {
      notifications.show({
        title: 'Navigator Assessment Required',
        message: 'Please select a completed Navigator assessment to link.',
        color: 'yellow'
      });
      return;
    }

    setIsStarting(true);

    try {
      let modelId;
      let modelName;

      if (assessmentType === 'builder') {
        if (modelSource === 'new') {
          // Create new model first
          const modelResult = await dispatch(createModel({
            modelName: newModelData.modelName,
            useCaseShortDescription: newModelData.useCaseShortDescription,
            modelType: newModelData.modelType,
            modelStage: newModelData.modelStage,
          })).unwrap();

          modelId = modelResult.model_uuid;
          modelName = newModelData.modelName;
        } else {
          modelId = form.values.selectedModel;
          const selectedModel = availableModels.find(m => m.modelId === modelId);
          modelName = selectedModel?.modelName || selectedModel?.model_name;
        }
      } else {
        // Navigator/Explorer: auto-resolve modelId from parent assessment
        modelId = form.values.selectedModel;
        const parentOptions = assessmentType === 'use_case' ? completedBuilderOptions : completedNavigatorOptions;
        const parentOption = parentOptions.find(a => a.value === parentAssessmentUuid);
        modelName = parentOption?.modelName || 'Unknown';
      }

      // Determine parent UUID based on type
      const linkedParent = (assessmentType === 'use_case' || assessmentType === 'business_case')
        ? (parentAssessmentUuid || null)
        : null;

      // Determine starting position based on type
      let startPosition = { majorStep: selectedMode === 'autopilot' ? 'upload' : 'useCase' };
      let startStep = selectedMode === 'autopilot' ? 'upload' : 'contextAssessment';
      if (assessmentType === 'use_case') {
        startPosition = { majorStep: 'describeUseCase' };
        startStep = 'describeUseCase';
      }

      // Create assessment with type
      const result = await dispatch(createAssessment({
        modelId,
        assessmentName: form.values.assessmentName.trim(),
        templateId: form.values.selectedTemplate,
        mode: selectedMode,
        autonomous: selectedMode === 'autopilot' ? isAutonomous : null,
        assessmentMode: selectedMode === 'autopilot' ? assessmentMode : 'prompt',
        currentStep: startStep,
        position: startPosition,
        assessmentType,
        parentAssessmentUuid: linkedParent,
      })).unwrap();

      const assessmentUuid = result.assessmentUuid;

      // Refresh data
      await Promise.all([
        dispatch(fetchAllModels()).unwrap(),
        dispatch(fetchAllAssessments()).unwrap()
      ]);

      onClose();

      // Navigate based on mode and assessment type
      if (assessmentType === 'use_case') {
        // Navigator assessments use their own flow
        navigate(`/navigator-assessment/${modelId}/${assessmentUuid}`);
      } else if (assessmentType === 'business_case') {
        // Explorer (business_case) assessments use Explorer autopilot flow
        navigate(`/explorer-assessment/${modelId}/${assessmentUuid}`);
      } else if (selectedMode === 'autopilot') {
        navigate(`/autopilot-assessment/${modelId}/${assessmentUuid}`);
      } else {
        navigate(`/risk-form/${modelId}/${encodeURIComponent(modelName)}/${assessmentUuid}`);
      }
    } catch (error) {
      console.error('Error starting assessment:', error);
      notifications.show({
        title: 'Error',
        message: `Failed to start assessment: ${error.message}`,
        color: 'red'
      });
    } finally {
      setIsStarting(false);
    }
  };

  // Model options with Builder status indicator for Explorer mode
  const modelOptions = availableModels.map(model => ({
    value: model.modelId,
    label: assessmentType === 'explorer' && model.builderAssessment
      ? `${model.modelName} (Builder ${model.builderAssessment.isCompleted ? '✓' : '◐'})`
      : `${model.modelName} (${model.modelType})`,
  }));

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={
        <Group gap="md" align="center">
          <IconRocket size={24} color="#1976d2" />
          <Title order={3}>Start New Risk Assessment</Title>
        </Group>
      }
      size="xl"
      centered
    >
      <Stack gap="xl">
        {/* 1. Assessment Type Selection */}
        <Stack gap="md">
          <Text fw={600} size="md">1. Assessment Type</Text>
          <Grid>
            <Grid.Col span={4} style={{ display: 'flex' }}>
              <Card
                withBorder
                radius="md"
                p="lg"
                style={{
                  cursor: 'pointer',
                  border: assessmentType === 'builder' ? '2px solid #1976d2' : '1px solid #dee2e6',
                  backgroundColor: assessmentType === 'builder' ? '#f0f7ff' : 'white',
                  flex: 1
                }}
                onClick={() => setAssessmentType('builder')}
              >
                <Stack gap="sm" align="center" justify="center" style={{ height: '100%' }}>
                  <IconTool size={28} color={assessmentType === 'builder' ? '#1976d2' : '#adb5bd'} />
                  <Text fw={700} size="md" c={assessmentType === 'builder' ? 'blue' : 'dimmed'}>Builder</Text>
                  <Text size="xs" ta="center" c="dimmed">
                    Technical audit of the model
                  </Text>
                </Stack>
              </Card>
            </Grid.Col>

            <Grid.Col span={4} style={{ display: 'flex' }}>
              <Card
                withBorder
                radius="md"
                p="lg"
                style={{
                  cursor: 'pointer',
                  border: assessmentType === 'use_case' ? '2px solid #fd7e14' : '1px solid #dee2e6',
                  backgroundColor: assessmentType === 'use_case' ? '#fff4e6' : 'white',
                  flex: 1
                }}
                onClick={() => setAssessmentType('use_case')}
              >
                <Stack gap="sm" align="center" justify="center" style={{ height: '100%' }}>
                  <IconCompass size={28} color={assessmentType === 'use_case' ? '#fd7e14' : '#adb5bd'} />
                  <Text fw={700} size="md" c={assessmentType === 'use_case' ? 'orange' : 'dimmed'}>Navigator</Text>
                  <Text size="xs" ta="center" c="dimmed">
                    Scenario-specific risks for a use case
                  </Text>
                </Stack>
              </Card>
            </Grid.Col>

            <Grid.Col span={4} style={{ display: 'flex' }}>
              <Card
                withBorder
                radius="md"
                p="lg"
                style={{
                  cursor: 'pointer',
                  border: assessmentType === 'business_case' ? '2px solid #40c057' : '1px solid #dee2e6',
                  backgroundColor: assessmentType === 'business_case' ? '#ebfbee' : 'white',
                  flex: 1
                }}
                onClick={() => setAssessmentType('business_case')}
              >
                <Stack gap="sm" align="center" justify="center" style={{ height: '100%' }}>
                  <IconGlobe size={28} color={assessmentType === 'business_case' ? '#40c057' : '#adb5bd'} />
                  <Text fw={700} size="md" c={assessmentType === 'business_case' ? 'green' : 'dimmed'}>Explorer</Text>
                  <Text size="xs" ta="center" c="dimmed">
                    Business impact: financial, regulatory, operational
                  </Text>
                </Stack>
              </Card>
            </Grid.Col>
          </Grid>
        </Stack>

        <Divider />

        {/* Conditional flows by type */}

        {assessmentType === 'builder' && (
          <>
            {/* Builder: Step 2 - Model Selection */}
            <Stack gap="md">
              <Text fw={600} size="md">2. Select Your Model</Text>

              {/* Model Source Toggle */}
              <Group gap="md">
                <Button
                  variant={modelSource === 'existing' ? 'filled' : 'light'}
                  color="blue"
                  size="sm"
                  onClick={() => setModelSource('existing')}
                >
                  Choose Existing Model
                </Button>
                <Button
                  variant={modelSource === 'new' ? 'filled' : 'light'}
                  color="green"
                  size="sm"
                  leftSection={<IconPlus size={16} />}
                  onClick={() => setModelSource('new')}
                >
                  Register New Model
                </Button>
              </Group>

              {modelSource === 'existing' ? (
                <>
                  <Select
                    placeholder="Choose a model to assess..."
                    data={modelOptions}
                    {...form.getInputProps('selectedModel')}
                    searchable
                    size="md"
                  />
                  {availableModels.length === 0 && (
                    <Alert color="blue" icon={<IconInfoCircle size="1rem" />}>
                      <Text size="sm">
                        All models already have Builder assessments. Register a new model or switch to Navigator/Explorer mode.
                      </Text>
                    </Alert>
                  )}
                </>
              ) : (
                <Card withBorder p="md" radius="md" bg="gray.0">
                  <Stack gap="md">
                    <Text fw={600} size="sm" c="green">Register New Model</Text>

                    <TextInput
                      label="Model Name"
                      required
                      placeholder="Enter model name..."
                      value={newModelData.modelName}
                      onChange={(e) => setNewModelData(prev => ({
                        ...prev, modelName: e.target.value
                      }))}
                    />

                    <TextInput
                      label="Short Description"
                      placeholder="Brief description of the model..."
                      value={newModelData.useCaseShortDescription}
                      onChange={(e) => setNewModelData(prev => ({
                        ...prev, useCaseShortDescription: e.target.value
                      }))}
                    />

                    <Group grow>
                      <Select
                        label="Model Type"
                        required
                        placeholder="Select type..."
                        data={['Generative AI', 'Predictive AI', 'Descriptive AI']}
                        value={newModelData.modelType}
                        onChange={(value) => setNewModelData(prev => ({
                          ...prev, modelType: value
                        }))}
                      />

                      <Select
                        label="Model Stage"
                        required
                        placeholder="Select stage..."
                        data={['Design', 'Development', 'Test', 'Inference/Production', 'Re-training']}
                        value={newModelData.modelStage}
                        onChange={(value) => setNewModelData(prev => ({
                          ...prev, modelStage: value
                        }))}
                      />
                    </Group>

                    <Divider label="Documentation" labelPosition="center" />

                    <Group gap="xs" align="flex-start" p="xs" style={{
                      backgroundColor: 'var(--mantine-color-blue-0)',
                      borderRadius: '8px'
                    }}>
                      <ThemeIcon size="sm" color="blue" variant="light" radius="xl">
                        <IconFileDescription size={14} />
                      </ThemeIcon>
                      <Stack gap={2} style={{ flex: 1 }}>
                        <Text size="xs" c="blue.7" fw={500}>
                          Model Documentation
                        </Text>
                        <Text size="xs" c="dimmed">
                          You can upload model card / documentation during the Autopilot workflow, or add it later from Model Inventory.
                        </Text>
                      </Stack>
                    </Group>

                    <Text size="xs" c="dimmed">
                      Model will be saved to Inventory when assessment starts.
                    </Text>
                  </Stack>
                </Card>
              )}
            </Stack>

            <Divider />

            {/* Builder: Step 3 - Name */}
            <Stack gap="md">
              <Text fw={600} size="md">3. Name Your Assessment</Text>
              <TextInput
                placeholder="Enter a unique name for this assessment..."
                {...form.getInputProps('assessmentName')}
                onChange={(event) => {
                  form.setFieldValue('assessmentName', event.currentTarget.value);
                  setUserEditedName(true);
                }}
                size="md"
                description="Give this assessment a descriptive name (auto-generated from model)"
              />
            </Stack>
          </>
        )}

        {assessmentType === 'use_case' && (
          <>
            {/* Navigator: Step 2 - Name */}
            <Stack gap="md">
              <Text fw={600} size="md">2. Name Your Assessment</Text>
              <TextInput
                placeholder="e.g., Customer Onboarding Use Case Risk Assessment"
                {...form.getInputProps('assessmentName')}
                onChange={(event) => {
                  form.setFieldValue('assessmentName', event.currentTarget.value);
                  setUserEditedName(true);
                }}
                size="md"
                required
                description="Give this Navigator assessment a descriptive name"
              />
            </Stack>

            <Divider />

            {/* Navigator: Step 3 - Select Completed Builder */}
            <Stack gap="md">
              <Text fw={600} size="md">3. Link to Builder Assessment</Text>
              <Text size="xs" c="dimmed">
                Navigator assessments evaluate scenario-specific risks based on a completed Builder assessment.
              </Text>

              {completedBuilderOptions.length > 0 ? (
                <>
                  <Select
                    placeholder="Choose a completed Builder assessment..."
                    data={completedBuilderOptions}
                    value={parentAssessmentUuid}
                    onChange={(value) => {
                      setParentAssessmentUuid(value || '');
                      const selected = completedBuilderOptions.find(a => a.value === value);
                      if (selected) {
                        form.setFieldValue('selectedModel', selected.modelUuid);
                      }
                    }}
                    searchable
                    size="md"
                  />

                  {parentAssessmentUuid && (
                    <Alert color="orange" icon={<IconCompass size="1rem" />} variant="light">
                      <Stack gap="xs">
                        <Text size="sm" fw={500}>
                          Technical risks will be imported from linked Builder
                        </Text>
                        <Text size="xs" c="dimmed">
                          Risks from the Builder assessment provide context for scenario-specific risk analysis.
                        </Text>
                      </Stack>
                    </Alert>
                  )}
                </>
              ) : (
                <Alert color="yellow" icon={<IconInfoCircle size="1rem" />}>
                  <Stack gap="xs">
                    <Text size="sm" fw={500}>No completed Builder assessments found</Text>
                    <Text size="xs" c="dimmed">
                      Complete a Builder assessment first to create a Navigator assessment.
                    </Text>
                  </Stack>
                </Alert>
              )}
            </Stack>
          </>
        )}

        {assessmentType === 'business_case' && (
          <>
            {/* Explorer: Step 2 - Name */}
            <Stack gap="md">
              <Text fw={600} size="md">2. Name Your Assessment</Text>
              <TextInput
                placeholder="e.g., Fraud Detection Business Impact Assessment"
                {...form.getInputProps('assessmentName')}
                onChange={(event) => {
                  form.setFieldValue('assessmentName', event.currentTarget.value);
                  setUserEditedName(true);
                }}
                size="md"
                required
                description="Give this Explorer assessment a descriptive name"
              />
            </Stack>

            <Divider />

            {/* Explorer: Step 3 - Select Completed Navigator */}
            <Stack gap="md">
              <Text fw={600} size="md">3. Link to Navigator Assessment</Text>
              <Text size="xs" c="dimmed">
                Explorer assessments evaluate business impact based on a completed Navigator assessment.
              </Text>

              {completedNavigatorOptions.length > 0 ? (
                <>
                  <Select
                    placeholder="Choose a completed Navigator assessment..."
                    data={completedNavigatorOptions}
                    value={parentAssessmentUuid}
                    onChange={(value) => {
                      setParentAssessmentUuid(value || '');
                      const selected = completedNavigatorOptions.find(a => a.value === value);
                      if (selected) {
                        form.setFieldValue('selectedModel', selected.modelUuid);
                      }
                    }}
                    searchable
                    size="md"
                  />

                  {parentAssessmentUuid && (
                    <Alert color="green" icon={<IconGlobe size="1rem" />} variant="light">
                      <Stack gap="xs">
                        <Text size="sm" fw={500}>
                          Use case risks will be imported from linked Navigator
                        </Text>
                        <Text size="xs" c="dimmed">
                          Scenario-specific risks from the Navigator assessment provide context for business impact analysis.
                        </Text>
                      </Stack>
                    </Alert>
                  )}
                </>
              ) : (
                <Alert color="yellow" icon={<IconInfoCircle size="1rem" />}>
                  <Stack gap="xs">
                    <Text size="sm" fw={500}>No completed Navigator assessments found</Text>
                    <Text size="xs" c="dimmed">
                      Complete a Navigator assessment first to create an Explorer assessment.
                    </Text>
                  </Stack>
                </Alert>
              )}
            </Stack>
          </>
        )}

        <Divider />

        {/* 4. Template Selection */}
        <Stack gap="md">
          <Text fw={600} size="md">4. Select Assessment Template</Text>
          <Select
            placeholder="Choose a template..."
            data={templateOptions}
            {...form.getInputProps('selectedTemplate')}
            searchable
            size="md"
            description="Choose a template with pre-configured risk areas and evaluation criteria"
          />
        </Stack>

        <Divider />

        {/* 5. Autopilot Execution Mode */}
        {(selectedMode === 'autopilot' || !userIsRiskManager) && (
          <Stack gap="md">
            <Text fw={600} size="md">{userIsRiskManager ? '5' : '5'}. Autopilot Execution Mode</Text>
            <Group gap="md" align="center" wrap="nowrap" style={{
              padding: '12px 16px',
              borderRadius: '8px',
              backgroundColor: 'var(--mantine-color-gray-0)',
              border: '1px solid var(--mantine-color-gray-3)',
              width: '100%',
              justifyContent: 'space-between'
            }}>
              <Stack gap={4} style={{ flex: 1 }}>
                <Text size="sm" fw={600} c={!isAutonomous ? "blue" : "dimmed"}>
                  Supervised
                </Text>
                <Text size="xs" c="dimmed">
                  Pause after each risk area for approval
                </Text>
              </Stack>

              <Switch
                checked={isAutonomous}
                onChange={(event) => setIsAutonomous(event.currentTarget.checked)}
                color="green"
                size="md"
                styles={{
                  track: {
                    backgroundColor: isAutonomous ? 'var(--mantine-color-green-6)' : 'var(--mantine-color-blue-6)',
                    cursor: 'pointer'
                  }
                }}
              />

              <Stack gap={4} style={{ flex: 1, alignItems: 'flex-end' }}>
                <Text size="sm" fw={600} c={isAutonomous ? "green" : "dimmed"}>
                  Autonomous
                </Text>
                <Text size="xs" c="dimmed" ta="right">
                  Process all areas automatically
                </Text>
              </Stack>
            </Group>
          </Stack>
        )}

        {/* 5b. Assessment Engine - Only for Risk Managers in Autopilot */}
        {userIsRiskManager && selectedMode === 'autopilot' && (
          <>
            <Divider />
            <Stack gap="md">
              <Text fw={600} size="md">Assessment Engine</Text>
              <Grid>
                <Grid.Col span={6}>
                  <Card
                    withBorder
                    radius="md"
                    p="lg"
                    style={{
                      cursor: 'pointer',
                      border: assessmentMode === 'prompt' ? '2px solid #1976d2' : '1px solid #dee2e6',
                      backgroundColor: assessmentMode === 'prompt' ? '#f0f7ff' : 'white',
                      minHeight: '140px'
                    }}
                    onClick={() => setAssessmentMode('prompt')}
                  >
                    <Stack gap="sm" align="center" justify="center" style={{ height: '100%' }}>
                      <IconBolt size={28} color={assessmentMode === 'prompt' ? '#1976d2' : '#adb5bd'} />
                      <Text fw={700} size="md" c={assessmentMode === 'prompt' ? 'blue' : 'dimmed'}>Prompt Pipeline</Text>
                      <Text size="xs" ta="center" c="dimmed">
                        Each risk area runs through a fixed sequence of prompts. Fast and predictable.
                      </Text>
                      {assessmentMode === 'prompt' && (
                        <Badge color="blue" variant="light" size="sm">Selected</Badge>
                      )}
                    </Stack>
                  </Card>
                </Grid.Col>

                <Grid.Col span={6}>
                  <Card
                    withBorder
                    radius="md"
                    p="lg"
                    style={{
                      cursor: 'pointer',
                      border: assessmentMode === 'agentic' ? '2px solid #7950f2' : '1px solid #dee2e6',
                      backgroundColor: assessmentMode === 'agentic' ? '#f3f0ff' : 'white',
                      minHeight: '140px'
                    }}
                    onClick={() => setAssessmentMode('agentic')}
                  >
                    <Stack gap="sm" align="center" justify="center" style={{ height: '100%' }}>
                      <IconRobot size={28} color={assessmentMode === 'agentic' ? '#7950f2' : '#adb5bd'} />
                      <Text fw={700} size="md" c={assessmentMode === 'agentic' ? 'violet' : 'dimmed'}>Agentic Pipeline</Text>
                      <Text size="xs" ta="center" c="dimmed">
                        AI agents collaborate autonomously with quality loops and tool access. More thorough, takes longer.
                      </Text>
                      {assessmentMode === 'agentic' && (
                        <Badge color="violet" variant="light" size="sm">Selected</Badge>
                      )}
                    </Stack>
                  </Card>
                </Grid.Col>
              </Grid>
            </Stack>
          </>
        )}

        {/* 6. Mode Selection - Only for Risk Managers */}
        {userIsRiskManager && (
          <>
            <Divider />
            <Stack gap="md">
              <Text fw={600} size="md">6. Choose Assessment Mode</Text>

              <Grid>
                <Grid.Col span={6}>
                  <Card
                    withBorder
                    radius="md"
                    p="lg"
                    style={{
                      cursor: 'pointer',
                      border: selectedMode === 'autopilot' ? '2px solid #1976d2' : '1px solid #dee2e6',
                      backgroundColor: selectedMode === 'autopilot' ? '#f0f7ff' : 'white'
                    }}
                    onClick={() => setSelectedMode('autopilot')}
                  >
                    <Stack gap="md" align="center">
                      <IconRobot size={32} color="#1976d2" />
                      <Text fw={700} size="lg" c="blue">Autopilot</Text>
                      <Text size="sm" ta="center" c="dimmed">
                        AI handles everything automatically
                      </Text>

                      {selectedMode === 'autopilot' ? (
                        <Badge color="blue" variant="light" fullWidth>
                          Selected
                        </Badge>
                      ) : (
                        <Button variant="light" color="blue" fullWidth size="sm">
                          Select
                        </Button>
                      )}
                    </Stack>
                  </Card>
                </Grid.Col>

                <Grid.Col span={6}>
                  <Card
                    withBorder
                    radius="md"
                    p="lg"
                    style={{
                      cursor: 'pointer',
                      border: selectedMode === 'copilot' ? '2px solid #495057' : '1px solid #dee2e6',
                      backgroundColor: selectedMode === 'copilot' ? '#f8f9fa' : 'white'
                    }}
                    onClick={() => setSelectedMode('copilot')}
                  >
                    <Stack gap="md" align="center">
                      <IconSettings size={32} color="#495057" />
                      <Text fw={700} size="lg" c="dark">Co-pilot</Text>
                      <Text size="sm" ta="center" c="dimmed">
                        You guide with AI help
                      </Text>

                      {selectedMode === 'copilot' ? (
                        <Badge color="dark" variant="light" fullWidth>
                          Selected
                        </Badge>
                      ) : (
                        <Button variant="light" color="dark" fullWidth size="sm">
                          Select
                        </Button>
                      )}
                    </Stack>
                  </Card>
                </Grid.Col>
              </Grid>
            </Stack>
            <Divider />
          </>
        )}

        {/* Info for non-Risk Managers */}
        {!userIsRiskManager && (
          <>
            <Divider />
            <Alert color="blue" icon={<IconRobot size="1rem" />}>
              <Text size="sm">
                Your assessment will start in <strong>Autopilot mode</strong>, where AI automatically handles the risk analysis process.
              </Text>
            </Alert>
            <Divider />
          </>
        )}

        {/* Action Buttons */}
        <Group justify="space-between">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>

          <Button
            onClick={handleStartAssessment}
            loading={isStarting}
            leftSection={selectedMode === 'autopilot' ? <IconRobot size={16} /> : <IconSettings size={16} />}
            size="md"
            color={assessmentType === 'builder' ? 'blue' : assessmentType === 'use_case' ? 'orange' : 'green'}
          >
            {isStarting
              ? 'Starting Assessment...'
              : `Start ${assessmentType === 'builder' ? 'Builder' : assessmentType === 'use_case' ? 'Navigator' : 'Explorer'} Assessment`
            }
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default StartAssessmentDialogV2;
